/**
 * AdminMasterComponentsPage - Slice B real editor.
 *
 * Single source of truth for design tokens: brand colors (with hex picker),
 * button styles, share icon (up arrow), Phygital logos (with image upload).
 *
 * Edits hit `PUT /api/admin/master-components` and the public reader
 * `GET /api/master-components` is consumed by `useMasterComponents` so any
 * surface that subscribes (header, buttons, share components) updates
 * instantly - no rebuild required.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { Palette, MousePointerClick, ArrowUp, Image as ImgIcon, Upload, Loader2, Save, RotateCcw, Folder, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import AdminMediaLibraryPage from "./AdminMediaLibraryPage";
import MasterComponentsCategoriesTab from "@/components/admin/MasterComponentsCategoriesTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const COLOR_KEYS = [
  { key: "primary",     label: "Primary" },
  { key: "accent",      label: "Accent" },
  { key: "success",     label: "Success" },
  { key: "warning",     label: "Warning" },
  { key: "error",       label: "Error" },
  { key: "background",  label: "Background" },
  { key: "text",        label: "Text" },
  { key: "text_muted",  label: "Text muted" },
];

const BUTTON_KEYS = [
  { key: "radius",      label: "Border radius",  hint: "e.g. 9999px (pill), 12px (rounded)" },
  { key: "padding_x",   label: "Padding X",      hint: "e.g. 1rem" },
  { key: "padding_y",   label: "Padding Y",      hint: "e.g. 0.5rem" },
  { key: "font_weight", label: "Font weight",    hint: "e.g. 500, 600" },
];

export default function AdminMasterComponentsPage() {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null); // 'light' | 'dark' | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/master-components`, { headers: auth() });
      if (r.ok) setDraft(await r.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Apply CSS variables instantly so the admin sees the change live
  useEffect(() => {
    if (!draft?.colors) return;
    const root = document.documentElement;
    Object.entries(draft.colors).forEach(([k, v]) => {
      root.style.setProperty(`--mc-${k.replace(/_/g, "-")}`, v);
    });
  }, [draft?.colors]);

  const updateGroup = (group, key, value) => {
    setDraft(d => ({ ...d, [group]: { ...(d?.[group] || {}), [key]: value } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        colors: draft.colors,
        buttons: draft.buttons,
        icons: draft.icons,
        logos: draft.logos,
      };
      const r = await fetch(`${API}/admin/master-components`, {
        method: "PUT",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        toast.success("Master Components saved - live across the platform");
        load();
      } else {
        toast.error("Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (!confirm("Reset to platform defaults? Saved values are kept until you click Save.")) return;
    load();
  };

  const handleLogoUpload = async (variant, file) => {
    if (!file) return;
    setUploading(variant);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "branding");
      const r = await fetch(`${API}/upload/cloudinary`, { method: "POST", headers: auth(), body: fd });
      if (!r.ok) throw new Error("Upload failed");
      const data = await r.json();
      updateGroup("logos", variant, data.secure_url || data.url);
      toast.success(`${variant} logo uploaded - click Save to persist`);
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(null);
    }
  };

  if (loading || !draft) {
    return <div className="flex items-center justify-center py-20" data-testid="mc-loading">
      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
    </div>;
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-master-components-page">
      <div className="p-6 max-w-5xl mx-auto">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900">Master Components</h1>
            <p className="text-sm text-stone-500 mt-1">
              Single source of truth for every brand color, button, icon, the Phygital logo, and the platform-wide media library. Edits propagate instantly to all surfaces.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={reset} data-testid="mc-reset"><RotateCcw className="w-3.5 h-3.5 mr-1.5" />Reset</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="mc-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
              Save & Publish
            </Button>
          </div>
        </div>

        <Tabs defaultValue="categories">
          <TabsList className="bg-stone-100 mb-4">
            <TabsTrigger value="categories" data-testid="mc-tab-categories"><Layers className="w-3.5 h-3.5 mr-1.5" />Categories</TabsTrigger>
            <TabsTrigger value="tokens" data-testid="mc-tab-tokens"><Palette className="w-3.5 h-3.5 mr-1.5" />Design tokens</TabsTrigger>
            <TabsTrigger value="media" data-testid="mc-tab-media"><Folder className="w-3.5 h-3.5 mr-1.5" />Media Library</TabsTrigger>
          </TabsList>
          <TabsContent value="categories" className="mt-0">
            <MasterComponentsCategoriesTab />
          </TabsContent>
          <TabsContent value="media" className="mt-0">
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
              <AdminMediaLibraryPage />
            </div>
          </TabsContent>
          <TabsContent value="tokens" className="mt-0 space-y-0">
        {/* Brand colors */}
        <section className="bg-white border border-stone-200 rounded-2xl mb-4 overflow-hidden" data-testid="mc-group-colors">
          <header className="flex items-center gap-2 px-5 py-3 border-b border-stone-100">
            <Palette className="w-4 h-4 text-stone-700" />
            <h2 className="text-sm font-semibold text-stone-900">Brand colors</h2>
          </header>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {COLOR_KEYS.map(({ key, label }) => {
              const v = draft.colors?.[key] || "#000000";
              return (
                <div key={key} className="flex items-center gap-3" data-testid={`mc-color-${key}`}>
                  <input
                    type="color"
                    value={v}
                    onChange={(e) => updateGroup("colors", key, e.target.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer border border-stone-200 shrink-0"
                    data-testid={`mc-color-picker-${key}`}
                  />
                  <div className="flex-1">
                    <Label className="text-xs text-stone-500">{label}</Label>
                    <Input
                      value={v}
                      onChange={(e) => updateGroup("colors", key, e.target.value)}
                      className="mt-1 h-8 font-mono text-xs"
                      data-testid={`mc-color-hex-${key}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Buttons */}
        <section className="bg-white border border-stone-200 rounded-2xl mb-4 overflow-hidden" data-testid="mc-group-buttons">
          <header className="flex items-center gap-2 px-5 py-3 border-b border-stone-100">
            <MousePointerClick className="w-4 h-4 text-stone-700" />
            <h2 className="text-sm font-semibold text-stone-900">Buttons</h2>
          </header>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {BUTTON_KEYS.map(({ key, label, hint }) => (
              <div key={key} data-testid={`mc-button-${key}`}>
                <Label className="text-xs text-stone-500">{label}</Label>
                <Input
                  value={draft.buttons?.[key] || ""}
                  onChange={(e) => updateGroup("buttons", key, e.target.value)}
                  className="mt-1 h-8 text-sm"
                  data-testid={`mc-button-input-${key}`}
                />
                <p className="text-[10px] text-stone-400 mt-1">{hint}</p>
              </div>
            ))}
            <div className="sm:col-span-2 flex items-center gap-3 pt-3 border-t border-stone-100">
              <span className="text-xs text-stone-500">Live preview:</span>
              <button
                style={{
                  borderRadius: draft.buttons?.radius,
                  padding: `${draft.buttons?.padding_y} ${draft.buttons?.padding_x}`,
                  fontWeight: draft.buttons?.font_weight,
                  background: draft.colors?.primary,
                  color: "#fff",
                }}
                data-testid="mc-button-preview"
              >
                Sample button
              </button>
            </div>
          </div>
        </section>

        {/* Share icon - up arrow ONLY (Iter 9.40+) */}
        <section className="bg-white border border-stone-200 rounded-2xl mb-4 overflow-hidden" data-testid="mc-group-icons">
          <header className="flex items-center gap-2 px-5 py-3 border-b border-stone-100">
            <ArrowUp className="w-4 h-4 text-stone-700" />
            <h2 className="text-sm font-semibold text-stone-900">Share icon</h2>
          </header>
          <div className="p-5">
            <p className="text-xs text-stone-500 mb-3">
              The up arrow <code className="px-1 rounded bg-stone-100">↑</code> is the canonical share component across the platform.
              The legacy node-graph share icon has been retired - this single token now drives every share affordance.
            </p>
            <div className="flex items-center gap-3 px-4 py-3 bg-stone-50 rounded-xl border border-stone-200">
              <div className="w-10 h-10 rounded-full bg-blue-700 text-white flex items-center justify-center">
                <ArrowUp className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-medium text-stone-900">ArrowUp · canonical share</p>
                <p className="text-[11px] text-stone-500">Used by Quick Actions, preference-card share, plan-share popup.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Logos */}
        <section className="bg-white border border-stone-200 rounded-2xl mb-6 overflow-hidden" data-testid="mc-group-logos">
          <header className="flex items-center gap-2 px-5 py-3 border-b border-stone-100">
            <ImgIcon className="w-4 h-4 text-stone-700" />
            <h2 className="text-sm font-semibold text-stone-900">Phygital logo</h2>
          </header>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {["light", "dark"].map((variant) => (
              <LogoUploader
                key={variant}
                variant={variant}
                value={draft.logos?.[variant]}
                onUpload={(file) => handleLogoUpload(variant, file)}
                uploading={uploading === variant}
              />
            ))}
            <div>
              <Label className="text-xs text-stone-500">Mobile placement</Label>
              <Input
                value={draft.logos?.placement_mobile || ""}
                onChange={(e) => updateGroup("logos", "placement_mobile", e.target.value)}
                className="mt-1 h-8 text-sm"
                data-testid="mc-logo-placement-mobile"
              />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Desktop placement</Label>
              <Input
                value={draft.logos?.placement_desktop || ""}
                onChange={(e) => updateGroup("logos", "placement_desktop", e.target.value)}
                className="mt-1 h-8 text-sm"
                data-testid="mc-logo-placement-desktop"
              />
            </div>
          </div>
        </section>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function LogoUploader({ variant, value, onUpload, uploading }) {
  const inputRef = useRef(null);
  const isDark = variant === "dark";
  return (
    <div data-testid={`mc-logo-${variant}`}>
      <Label className="text-xs text-stone-500 block mb-1.5">
        Logo · {variant === "light" ? "for light backgrounds" : "for dark backgrounds"}
      </Label>
      <div
        className={`flex items-center justify-center h-24 rounded-xl border-2 border-dashed ${isDark ? "bg-stone-900 border-stone-700" : "bg-stone-50 border-stone-200"} mb-2`}
      >
        {value ? (
          <img src={value} alt={`logo-${variant}`} className="max-h-16 max-w-[80%] object-contain" />
        ) : (
          <span className={`text-xs ${isDark ? "text-stone-400" : "text-stone-400"}`}>No logo set</span>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onUpload(e.target.files?.[0])}
        data-testid={`mc-logo-file-${variant}`}
      />
      <Button
        variant="outline"
        size="sm"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full"
        data-testid={`mc-logo-upload-${variant}`}
      >
        {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
        Upload {variant} logo
      </Button>
    </div>
  );
}
